/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { simpleFetch } from "../../restWrapper";
import "./ExportMenu.css";

const ALL_PAYMENT_MODES = ["manual", "automatic", "payout"];

class ExportMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      showCustomRange: false,
      startDate: null,
      endDate: null,
      sending: false,
      message: "",
      messageTone: "",
    };
    this.toggleOpen = this.toggleOpen.bind(this);
    this.showCustomRange = this.showCustomRange.bind(this);
    this.cancelCustomRange = this.cancelCustomRange.bind(this);
    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.confirmCustomRange = this.confirmCustomRange.bind(this);
    this.fireLifetimeTransactionExport = this.fireLifetimeTransactionExport.bind(this);
    this.fireProjectSummary = this.fireProjectSummary.bind(this);
  }

  t = (key, fallback) => {
    const block =
      this.props.languageObj && typeof this.props.languageObj === "object"
        ? this.props.languageObj
        : {};
    return block[key] || fallback;
  };

  toggleOpen() {
    this.setState((prev) => ({
      open: !prev.open,
      showCustomRange: false,
      message: "",
      messageTone: "",
    }));
  }

  showCustomRange() {
    this.setState({ showCustomRange: true });
  }

  cancelCustomRange() {
    this.setState({ showCustomRange: false, startDate: null, endDate: null });
  }

  handleRangeChange(update) {
    const [start, end] = update;
    this.setState({ startDate: start, endDate: end });
  }

  // Exports POST their params in the JSON body (CP-241): a selected-projects
  // export carries the ids as an array, and at a few hundred projects that
  // overflows a GET query string — production's WAF blocks query strings
  // over 2KB, so large selections never even reached the backend.
  runExport(path, extraBody) {
    const body = { ...extraBody };
    const ids = this.props.projectIds || [];
    if (ids.length > 0) body.projectIds = ids;

    this.setState({ sending: true, message: "", messageTone: "" });
    return simpleFetch(path, "POST", body)
      .then((res) => {
        const ok = res.status === 200 || res.status === 204;
        this.setState({
          sending: false,
          open: false,
          showCustomRange: false,
          message: ok
            ? this.t("exportSentMsg", "Check your email — your export is on its way.")
            : this.t("exportFailedMsg", "Export failed. Please try again."),
          messageTone: ok ? "success" : "error",
        });
      })
      .catch(() => {
        this.setState({
          sending: false,
          open: false,
          showCustomRange: false,
          message: this.t("exportFailedMsg", "Export failed. Please try again."),
          messageTone: "error",
        });
      });
  }

  fireLifetimeTransactionExport() {
    const moment = require("moment");
    const startDate = moment(new Date(0)).startOf("day").toISOString(false);
    const endDate = moment().endOf("day").toISOString(false);
    this.runExport("/organizations/listTransactions", {
      startDate,
      endDate,
      paymentModes: ALL_PAYMENT_MODES,
    });
  }

  confirmCustomRange() {
    if (!this.state.startDate) return;
    const moment = require("moment");
    const startDate = moment(this.state.startDate).startOf("day").toISOString(false);
    const endDate = moment(this.state.endDate || this.state.startDate)
      .endOf("day")
      .toISOString(false);
    this.runExport("/organizations/listTransactions", {
      startDate,
      endDate,
      paymentModes: ALL_PAYMENT_MODES,
    });
  }

  fireProjectSummary() {
    this.runExport("/organizations/projectSummary", {});
  }

  render() {
    const {
      open,
      showCustomRange,
      startDate,
      endDate,
      sending,
      message,
      messageTone,
    } = this.state;
    const { disabled, alignRight } = this.props;
    const selectPrompt = this.t(
      "exportSelectPrompt",
      "Select at least one project to export"
    );
    return (
      <div
        className={`ExportMenu${disabled ? " ExportMenu--disabled" : ""}${
          alignRight ? " ExportMenu--alignRight" : ""
        }`}
        title={disabled ? selectPrompt : undefined}
        data-tooltip={disabled ? selectPrompt : undefined}
      >
        <button
          type="button"
          className="ExportMenu-trigger"
          onClick={this.toggleOpen}
          disabled={disabled}
        >
          {this.t("exportBtn", "Export")} ▾
        </button>

        {open && !disabled ? (
          <React.Fragment>
            <div className="ExportMenu-overlay" onClick={this.toggleOpen} />
            <div
              className={`ExportMenu-dropdown${
                alignRight ? " ExportMenu-dropdown--right" : ""
              }`}
            >
              {!showCustomRange ? (
                <React.Fragment>
                  <button
                    type="button"
                    className="ExportMenu-item"
                    onClick={this.fireLifetimeTransactionExport}
                    disabled={sending}
                  >
                    {this.t("exportTransactionLifetime", "Transaction Export (Lifetime)")}
                  </button>
                  <button
                    type="button"
                    className="ExportMenu-item"
                    onClick={this.showCustomRange}
                    disabled={sending}
                  >
                    {this.t("exportTransactionCustomRange", "Transaction Export (Custom Range)")}
                  </button>
                  <button
                    type="button"
                    className="ExportMenu-item"
                    onClick={this.fireProjectSummary}
                    disabled={sending}
                  >
                    {this.t("exportProjectSummary", "Project Summary")}
                  </button>
                </React.Fragment>
              ) : (
                <div className="ExportMenu-customRange">
                  <div className="ExportMenu-dateField">
                    <div className="ExportMenu-dateFieldInner">
                      <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={new Date()}
                        className="ExportMenu-datePicker"
                        onChange={this.handleRangeChange}
                        dateFormat="yyyy/MM/dd"
                        isClearable
                        placeholderText={this.t(
                          "exportDatePlaceholder",
                          "Pick a date range"
                        )}
                      />
                    </div>
                  </div>
                  <div className="ExportMenu-customRangeActions">
                    <button
                      type="button"
                      className="ExportMenu-applyBtn"
                      onClick={this.confirmCustomRange}
                      disabled={sending || !startDate}
                    >
                      {this.t("apply", "Apply")}
                    </button>
                    <button
                      type="button"
                      className="ExportMenu-linkBtn"
                      onClick={this.cancelCustomRange}
                      disabled={sending}
                    >
                      {this.t("cancel", "Cancel")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </React.Fragment>
        ) : null}

        {message ? (
          <div
            className={`ExportMenu-message${
              messageTone === "error"
                ? " ExportMenu-message--error"
                : " ExportMenu-message--success"
            }`}
            role="status"
          >
            {message}
          </div>
        ) : null}
      </div>
    );
  }
}

ExportMenu.propTypes = {
  projectIds: PropTypes.array,
  languageObj: PropTypes.object,
  disabled: PropTypes.bool,
  alignRight: PropTypes.bool,
};

ExportMenu.defaultProps = {
  projectIds: [],
  disabled: false,
  alignRight: false,
};

export default ExportMenu;
